import classNames from "classnames";
import { useMotionValueEvent, useScroll } from "framer-motion";
import { ReactNode, useEffect, useRef, useState } from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useActionExecutor } from "../../customeHooks/useActionExecutor";
import { baseApi } from "../../redux/api";
import { authSliceActions } from "../../redux/features/auth/auth.slice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { LANDING_PAGE_URL, resumeUrlKey } from "../../utils/constants";
import { isClientAuthCookieSet } from "../../utils/helpers";
import { ScrollToTop } from "../scrollToTop";
import { HelpWidget } from "./components/app/helpWidget";


export const AuthLayout = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const location = useLocation();

  const container = useRef<HTMLDivElement>(null);
  const helpWidgetRef = useRef<HTMLButtonElement>(null);

  const [isFloating, setIsFloating] = useState(false);

  const { isSignedIn } = useAppSelector((state) => state.auth);

  const { action } = useActionExecutor();

  const dispatch = useAppDispatch();

  const { scrollY } = useScroll({ container });

  useMotionValueEvent(scrollY, "change", (val) => {
    setIsFloating(val > 24);
  });

  useEffect(() => {
    const resume = searchParams.get("resume")?.trim();

    // Set resume url if any
    if (resume) {
      localStorage.setItem(resumeUrlKey, resume);
      setSearchParams({});
    }

    const isAuthSet = isClientAuthCookieSet();

    if (isAuthSet && isSignedIn && action === "accept-invite") {
      dispatch(authSliceActions.signUserOut());
      return;
    }

    if (isAuthSet && isSignedIn) {
      const resumeUrl = localStorage.getItem(resumeUrlKey);
      if (resumeUrl) {
        navigate(resumeUrl);
        localStorage.removeItem(resumeUrlKey);
      } else {
        navigate("/home");
      }
    } else {
      dispatch(baseApi.util.resetApiState());
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  return (
    <>
      <ScrollToTop
        changeKey={location.pathname}
        rerunOnKeyChange
        scrollIntoView
      />

      <div className="relative flex h-screen max-h-[100dvh] flex-col overflow-clip bg-[linear-gradient(180deg,#D5E9FC_0%,#F4F7FA_45%)]">
        <img
          src="/assets/images/landing-testimonial-user-glass.png"
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-[100px] -top-10 hidden size-[280px] rotate-[13.5deg] object-cover md:block lg:-right-[161px] lg:-top-16 lg:size-[352px]"
        />
        <img
          src="/assets/images/landing-why-party-popper.png"
          alt=""
          aria-hidden
          className="pointer-events-none absolute -bottom-20 -left-16 hidden size-[280px] -scale-y-100 rotate-[-172.43deg] object-cover md:block lg:-bottom-[137px] lg:-left-[102px] lg:size-[394px]"
        />

        <div
          ref={container}
          className="relative z-[1] flex h-full flex-col overflow-y-scroll"
        >
          <div
            className={classNames(
              "sticky top-0 z-[30] w-full bg-transparent transition-all duration-500 ease-in-out",
              { "pt-3": isFloating },
            )}
          >
            <div
              className={classNames(
                "mx-auto flex flex-col overflow-hidden transition-all duration-500 ease-in-out",
                isFloating
                  ? "w-[calc(100%-32px)] max-w-[800px] rounded-full bg-white shadow-[0px_1px_2px_0px_rgba(6,14,53,0.08),0px_12px_12px_0px_rgba(6,14,53,0.04)]"
                  : "max-width-website",
              )}
            >
              <div
                className={classNames(
                  "flex items-center transition-all duration-500 ease-in-out",
                  isFloating ? "py-3 pl-5 pr-3" : "py-5",
                )}
              >
                <NavLink to={LANDING_PAGE_URL} className="w-[88px] shrink-0">
                  <img
                    src="/assets/logo_full.svg"
                    alt="logo"
                    className="w-full"
                  />
                </NavLink>
              </div>
            </div>
          </div>

          <div id="pageTopRefToScroll" className="h-0" />

          <div className="flex flex-1 justify-center px-5 py-10 md:items-center md:px-8 md:py-16">
            <div className="h-fit w-full max-w-[400px] lg:my-auto">
              {children}
            </div>
          </div>
        </div>
      </div>

      <div className="!fixed bottom-4 right-4 z-[999] w-fit md:bottom-7 md:right-10">
        <HelpWidget helpWidgetRef={helpWidgetRef} />
      </div>
    </>
  );
};
